import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, Save } from 'lucide-react';
import { useRates } from '../hooks/useRates';
import { Input, Select } from '@/components/ui';
import type { ResourceRate, RateCreatePayload } from '../types/rates.types';
import { supabase } from '@/core/database/supabase';
import { format } from 'date-fns';

interface RateModalProps {
  isOpen: boolean;
  onClose: () => void;
  rate?: ResourceRate;
}

export function RateModal({ isOpen, onClose, rate }: RateModalProps) {
  const { createRate, updateRate, isCreating, isUpdating } = useRates();
  const isSaving = isCreating || isUpdating;

  const [resources, setResources] = useState<{id: string, name: string, code: string, unit: string}[]>([]);
  const [suppliers, setSuppliers] = useState<{id: string, name: string}[]>([]);

  useEffect(() => {
    if (isOpen) {
      supabase.from('resources').select('id, name, code, unit').then(({ data }) => setResources(data || []));
      supabase.from('suppliers').select('id, name').then(({ data }) => setSuppliers(data || []));
    }
  }, [isOpen]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RateCreatePayload>({
    defaultValues: {
      resource_id: '',
      supplier_id: '',
      price: 0,
      waste_percent: 0,
      vat_percent: 0,
      effective_from: format(new Date(), 'yyyy-MM-dd'),
      status: 'active',
      note: '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (rate) {
        reset({
          resource_id: rate.resource_id,
          supplier_id: rate.supplier_id || '',
          price: rate.price,
          waste_percent: rate.waste_percent || 0,
          vat_percent: rate.vat_percent || 0,
          effective_from: rate.effective_from ? format(new Date(rate.effective_from), 'yyyy-MM-dd') : '',
          status: rate.status,
          note: rate.note || '',
        });
      } else {
        reset({
          resource_id: '',
          supplier_id: '',
          price: 0,
          waste_percent: 0,
          vat_percent: 0,
          effective_from: format(new Date(), 'yyyy-MM-dd'),
          status: 'active',
          note: '',
        });
      }
    }
  }, [isOpen, rate, reset]);

  if (!isOpen) return null;

  const onSubmit = async (data: RateCreatePayload) => {
    const payload = {
      ...data,
      supplier_id: data.supplier_id || null,
      price: Number(data.price),
      waste_percent: data.waste_percent ? Number(data.waste_percent) : 0,
      vat_percent: data.vat_percent ? Number(data.vat_percent) : 0,
      effective_from: data.effective_from ? new Date(data.effective_from).toISOString() : null,
    };

    if (rate) {
      await updateRate({ id: rate.id, payload });
    } else {
      await createRate(payload);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-white rounded-lg shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">
            {rate ? 'Cập nhật đơn giá' : 'Thêm mới đơn giá'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <form id="rate-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                Tài nguyên <span className="text-rose-500">*</span>
              </label>
              <Select {...register('resource_id', { required: 'Vui lòng chọn tài nguyên' })} className="w-full sm:text-sm text-xs">
                <option value="">-- Chọn tài nguyên --</option>
                {resources.map(r => (
                  <option key={r.id} value={r.id}>
                    [{r.code}] {r.name} ({r.unit})
                  </option>
                ))}
              </Select>
              {errors.resource_id && <p className="mt-1 text-xs text-rose-500">{errors.resource_id.message}</p>}
            </div>

            <div>
              <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                Nhà cung cấp
              </label>
              <Select {...register('supplier_id')} className="w-full sm:text-sm text-xs">
                <option value="">-- Có thể để trống --</option>
                {suppliers.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                Đơn giá (VNĐ) <span className="text-rose-500">*</span>
              </label>
              <Input
                type="number"
                min={0}
                {...register('price', { required: 'Vui lòng nhập đơn giá' })}
                placeholder="0"
                className="w-full sm:text-sm text-xs font-mono"
              />
              {errors.price && <p className="mt-1 text-xs text-rose-500">{errors.price.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                  Thuế VAT (%)
                </label>
                <Input
                  type="number"
                  min={0}
                  step={0.1}
                  {...register('vat_percent')}
                  className="w-full sm:text-sm text-xs"
                />
              </div>
              <div>
                <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                  Hao hụt (%)
                </label>
                <Input
                  type="number"
                  min={0}
                  step={0.1}
                  {...register('waste_percent')}
                  className="w-full sm:text-sm text-xs"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                  Hiệu lực từ
                </label>
                <Input
                  type="date"
                  {...register('effective_from')}
                  className="w-full sm:text-sm text-xs"
                />
              </div>
              <div>
                <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                  Trạng thái
                </label>
                <Select {...register('status')} className="w-full sm:text-sm text-xs">
                  <option value="active">Hiệu lực</option>
                  <option value="inactive">Ngừng</option>
                </Select>
              </div>
            </div>

            <div>
              <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                Ghi chú
              </label>
              <Input
                {...register('note')}
                placeholder="Điều kiện vận chuyển, bốc xếp..."
                className="w-full sm:text-sm text-xs"
              />
            </div>
          </form>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-200 bg-slate-50 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 sm:text-sm text-xs font-medium text-slate-700 hover:bg-slate-200 rounded-md transition-colors"
          >
            Hủy bỏ
          </button>
          <button
            type="submit"
            form="rate-form"
            disabled={isSaving}
            className="inline-flex items-center justify-center gap-2 px-6 py-2 sm:text-sm text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Đang lưu...' : 'Lưu lại'}
          </button>
        </div>
      </div>
    </div>
  );
}
